// HOW A LIST OF ROWS BECOMES TWO COLUMNS (busy-my-web#530).
//
// Saffet, 2026-09-17: "First we must list the 5 in the briefing on the left then 3 more to the
// right so the left has a full list then continues to the right."
//
// That is COLUMN-FIRST flow: the left column is filled to its cap, and only what is left over
// starts the right one — so a Briefing with five rows or fewer draws one full column and an empty
// second, and the sixth row is the first thing on the right. It is the opposite of what a CSS
// two-column grid does on its own (`grid-flow-row` lays 1 2 / 3 4 / 5 6 ACROSS), and it is also not
// what the card did before this issue: rows went left or right by MEMBERSHIP of a key list
// (`EMAIL_LEFT_COLUMN`, busy-my-web#338), which meant a mailbox with nothing in one of the left
// entries drew four rows on the left and five on the right.
//
// It is done here, in a pure module, rather than with `grid-flow-col` + `grid-rows-5` in the
// template, for the reason every other briefing rule lives in a lib: the split is a decision about
// WHAT THE CARD SAYS (which rows a reader meets first), it has to hold identically in the three
// places the entries are drawn (the strip, home's modal, the folder card), and a rule written in
// utility classes can only be checked by mounting something.

/** How many rows the left column takes before the next one starts the right (Saffet's five). */
export const LEFT_COLUMN_MAX = 5

/**
 * Split rows into `[left, right]`, filling the left column FIRST.
 *
 * @param {Array<object>} rows  the rows in reading order; nulls are dropped (a row with no count
 *                              is not a row, and it must not take up one of the five)
 * @param {number} leftMax      how many rows the left column holds
 * @returns {[Array<object>, Array<object>]}
 */
export function splitColumns(rows, leftMax = LEFT_COLUMN_MAX) {
  const list = (rows || []).filter(Boolean)
  // A cap of zero or less would put every row on the right and leave the left one empty, which is
  // never what "fill the left first" means; the list is then one column, as it is with one row.
  const cap = Number.isFinite(Number(leftMax)) && Number(leftMax) > 0 ? Math.floor(Number(leftMax)) : list.length
  return [list.slice(0, cap), list.slice(cap)]
}

/**
 * The same split, interleaved into the order a two-column CSS grid has to receive the rows in to
 * READ DOWN each column, each row stamped with the column it belongs to (`col: 1 | 2`).
 *
 * The stamp is what keeps the flow true when the columns are uneven: the interleave alone would
 * push a left-hand row into whatever cell came next, and the strip turns `col` into that cell's own
 * `col-start` so a row with a definite column is never placed in the other one.
 */
export function columnGridOrder(rows, leftMax = LEFT_COLUMN_MAX) {
  const [left, right] = splitColumns(rows, leftMax)
  const out = []
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    if (left[i]) out.push({ ...left[i], col: 1 })
    if (right[i]) out.push({ ...right[i], col: 2 })
  }
  return out
}

/** How many facts a creation-briefing column holds before the next one starts (#641). */
export const FACTS_PER_COLUMN = 4

/**
 * THE CREATION BRIEFING'S FACTS, COLUMN-MAJOR IN FIXED-HEIGHT COLUMNS (busy-my-web#641, Pouria
 * 2026-10-16; Figma 40008539:200623). Column 1 fills top to bottom up to `perColumn` rows, the next
 * fact starts column 2, and so on — five facts are 4 + 1, nine are 4 + 4 + 1. The same flow as
 * `splitColumns`, with any number of columns rather than two.
 *
 * Done here and not with `grid-auto-flow: column; grid-template-rows: repeat(4, auto)` because a
 * grid with fewer facts than rows still lays out the empty tracks and their row gaps, so a one-fact
 * strip would grow by three gaps of nothing.
 *
 * @param {Array<object>} rows   the rows in reading order; nulls are dropped
 * @param {number} perColumn     rows per column (a non-positive cap means one column)
 * @returns {Array<Array<object>>} the columns, none of them empty
 */
export function columnMajor(rows, perColumn = FACTS_PER_COLUMN) {
  const list = (rows || []).filter(Boolean)
  const cap = Number.isFinite(Number(perColumn)) && Number(perColumn) > 0 ? Math.floor(Number(perColumn)) : list.length
  const out = []
  for (let i = 0; i < list.length; i += cap) out.push(list.slice(i, i + cap))
  return out
}

export default splitColumns
